//WebMaps.ts =================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as WidgetHeading from "./WidgetHeading";

import { Button, ButtonGroup, Icon, Paper, Typography } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";

import React from "react";

import Point from "@arcgis/core/geometry/Point";
import * as projection from "@arcgis/core/geometry/projection";
import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";

const toolName: string = "Web Maps";
let showPictometry: boolean = false; //Custom!
let showNearmap: boolean = false; //Custom!

let click_eventHandler: IHandle;

//Module variables.
let clickPnt: __esri.Point = null;
let heading: number = 0;
let activeTool: string = "Street View";
let helpText: string = "Click on the map for Google Street View";

let googleMapsColor: ButtonProps["color"] = "primary";
let streetViewColor: ButtonProps["color"] = "secondary";
let googleEarthColor: ButtonProps["color"] = "primary";
let bingMapsColor: ButtonProps["color"] = "primary";
let streetsideColor: ButtonProps["color"] = "primary";
let pictometryColor: ButtonProps["color"] = "primary";
let nearmapColor: ButtonProps["color"] = "primary";

let newWindow: Window;

//Clear this tool.
export function clearTool() {
}

//Input arguments.
interface iProps {
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of WebMaps.Render");

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      Util.logMsg("WebMaps mounted");

      click_eventHandler = MainMap.activeView.on("click", function (event) {
         clickPnt = event.mapPoint;
         processMapClick();
      });

      //Component will unmount.
      return () => {
         click_eventHandler.remove();
      };
   }, []);

   //Component did update.
   React.useEffect(() => {
   });

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Google maps.
   function googleMaps() {
      setActiveTool("Google Maps");
      showResults();
   }

   //Street view.
   function streetView() {
      setActiveTool("Street View");
      showResults();
   }

   //Google earth.
   function googleEarth() {
      setActiveTool("Google Earth");
      showResults();
   }

   //Bing maps.
   function bingMaps() {
      setActiveTool("Bing Maps");
      showResults();
   }

   //Streetside.
   function streetside() {
      setActiveTool("Streetside");
      showResults();
   }

   //Pictometry.
   function pictometry() {
      setActiveTool("Pictometry");
      showResults();
   }

   //Nearmap. CUSTOM! Poway
   function nearmap() {
      setActiveTool("Nearmap");
      showResults();
   }

   //Set active tool.
   function setActiveTool(name: string) {
      activeTool = name;

      googleMapsColor = "primary";
      streetViewColor = "primary";
      googleEarthColor = "primary";
      bingMapsColor = "primary";
      streetsideColor = "primary";
      pictometryColor = "primary";
      nearmapColor = "primary";

      if (name === "Google Maps") {
         googleMapsColor = "secondary";
         helpText = "Click on the map for Google Maps.";

      } else if (name === "Street View") {
         streetViewColor = "secondary";
         helpText = "Click on the map for Google Street View.";

      } else if (name === "Google Earth") {
         googleEarthColor = "secondary";
         helpText = "Click on the map for Google Earth.";

      } else if (name === "Bing Maps") {
         bingMapsColor = "secondary";
         helpText = "Click on the map for Bing Maps.";

      } else if (name === "Streetside") {
         streetsideColor = "secondary";
         helpText = "Click on the map for Bing Streetside.";

      } else if (name === "Pictometry") {
         pictometryColor = "secondary";
         helpText = "Click on the map for oblique imagery.";

      } else if (name === "Nearmap") {
         nearmapColor = "secondary";
         helpText = "Click on the map for Nearmap MapBrowser.";
      }
      refresh();
   }

   //Process tool.
   function processMapClick() {
      if (activeTool === "Street View" || activeTool === "Streetside" || activeTool === "Pictometry") {
         getHeading();
      } else {
         showResults();
      }
   }

   //Get orientation angle for required tools.
   function getHeading() {
      const laySpec: Settings.iLayer = Util_App.getLayerSpec(Settings.streetCenterlinesLayerName);
      if (!laySpec) {
         Util.errMsg("Layer setting is missing for " + Settings.streetCenterlinesLayerName);
         return;
      }

      //Build query.
      const query = new Query({
         returnGeometry: true,
         outSpatialReference: MainMap.activeView.spatialReference,
         geometry: clickPnt,
         maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
         geometryPrecision: 0,
         outFields: ["OBJECTID"],
         distance: 500,
         units: "feet",
      });

      const url: string = Util_App.getLayerURL(laySpec);
      QueryTask.executeQueryJSON(url, query).then(getNearCenterlines_completed).catch(queryTask_failed);
   }

   //Get near street centerlines completed event.
   function getNearCenterlines_completed(fs: __esri.FeatureSet) {

      if (fs.features.length > 0) {
         let closePnt: __esri.Point;
         let closeDist: number = 999999999;

         for (const feature of fs.features) {
            const geom = Util_App.geometryEngine.densify(feature.geometry, 20, "feet");

            const nearPnt = Util_App.geometryEngine.nearestVertex(geom, clickPnt);
            if (nearPnt.distance < closeDist) {
               closeDist = nearPnt.distance;
               closePnt = nearPnt.coordinate;
            }
         }

         heading = Util_App.getAngle(closePnt, clickPnt, true);
         //Util.errMsg("Heading: " + heading);

         //Move the click point closer to the road.
         if (closeDist > 100) {
            clickPnt = new Point({ x: (closePnt.x + clickPnt.x) / 2, y: (closePnt.y + clickPnt.y) / 2, spatialReference: MainMap.activeView.spatialReference });
         }

         showResults();

      } else { //No features found.
         heading = 0;
         showResults();
      }
   }

   //Query task failed.
   function queryTask_failed(error: Error): void {
      Util.errMsg("QueryTask for street centerlines failed.", error);
   }

   //Show the results.
   function showResults() {
      if (!clickPnt) {
         return;
      }

      //Project to lat/long.
      const outSpatialReference = new SpatialReference({ wkid: 4326 });
      const geom = projection.project(clickPnt, outSpatialReference) as __esri.Point;
      const latY: number = geom.y;
      const lonX: number = geom.x;
      const latYRound: number = Math.round(geom.y * 10000000) / 10000000; /*CUSTOM! Poway*/
      const lonXRound: number = Math.round(geom.x * 10000000) / 10000000; /*CUSTOM! Poway*/

      let url: string;

      if (activeTool === "Google Maps") {
         //You don't need a Google API key to use Maps URLs.
         //https://developers.google.com/maps/documentation/urls/guide
         //zoom: zoom level, basemap: basemap, center: lat,lon of center point.
         url = "https://www.google.com/maps/@?api=1&map_action=map&zoom=18&basemap=satellite&center=" + latY + "," + lonX;

      } else if (activeTool === "Street View") {
         //You don't need a Google API key to use Maps URLs.
         //https://developers.google.com/maps/documentation/urls/guide
         //viewpoint: lat,lon of closest picture, heading: clockwise degrees from north
         url = "https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=" + latY + "," + lonX;
         url += "&heading=" + heading;

      } else if (activeTool === "Google Earth") {
         //a: altitude, d: distance to target, y: field of view, h: heading, t: tilt, r: roll.
         url = "https://earth.google.com/web/@" + latY + "," + lonX + ",150a,200d,35y,0h,50t,0r";

      } else if (activeTool === "Bing Maps") {
         //https://docs.microsoft.com/en-us/bingmaps/articles/create-a-custom-map-url
         //CenterPoint: cp=lat~lon, ZoomLevel: lvl=18, Style: style=h (aerials with labels)
         url = "https://www.bing.com/maps?lvl=18&style=h&cp=" + latY + "~" + lonX;

      } else if (activeTool === "Streetside") {
         //https://docs.microsoft.com/en-us/bingmaps/articles/create-a-custom-map-url
         //CenterPoint: cp=lat~lon, ZoomLevel: lvl=18, Style: style=x (streetside), dir: degrees
         url = "https://www.bing.com/mapspreview?lvl=17&style=x&cp=" + latY + "~" + lonX;
         url += "&dir=" + heading;

      } else if (activeTool === "Pictometry") {
         //PictometryIPA or PictometryAnalytics. Custom!
         url = Settings.webServerURL + "/PictometryIPA" + "?Lat=" + latY + "&Lon=" + lonX + "&heading=" + heading;

      } else if (activeTool === "Nearmap") {
         //Nearmap MapBrowser. CUSTOM! Poway
         url = "https://apps.nearmap.com/maps/#/@" + latYRound + "," + lonXRound + ",19.00z";
      }

      window.open(url, "WebMaps"); //Too bad this has to always open in a new window.

      // Zillow "https://www.zillow.com/homes/for_sale/latY,lonX,latY,lonX_rect/17_zm/";
      // Bird’s Eye http://www.bing.com/maps/default.aspx?v=2&FORM=LMLTCP&cp=46.0247336671418~-95.2639333729392&style=b&
   }

   //Icons.
   const pictometryIcon = <Icon style={{ fontSize: 17 }}>airplanemode_active</Icon>;

   if (Settings.security.accessLevel < 0) {
      showPictometry = false; //Custom!
   }

   /*CUSTOM! Poway*/
   if (Settings.security.accessLevel >= 25) {
      showNearmap = true;
   }

   //Styles.
   const styles: Default.iStyles = {
      header: { backgroundColor: Settings.headingColor },
      help: { marginTop: 5 },
      toolStyle: { width: Settings.leftPanelWidth, maxWidth: Settings.leftPanelWidth, padding: 2, paddingBottom: 9, overflow: 'auto' },
      button: { paddingLeft: 5, paddingRight: 5 },
      titleCell: { fontWeight: 500 },
   };

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div style={styles.toolStyle}>
            <Typography style={styles.help}>{helpText}</Typography>

            <div className="app-flex-row-center" style={{ marginTop: 5 }}>
               <ButtonGroup variant="contained" aria-label="Google toolbar buttons">
                  <Button style={styles.button} color={googleMapsColor} onClick={googleMaps} title="Google Maps" aria-label="Google Maps">
                     Google Maps
                  </Button>

                  <Button style={styles.button} color={streetViewColor} onClick={streetView}
                     title="For best results, click a point between a road and the area to be viewed." aria-label="Street View">
                     Street View
                  </Button>

                  <Button style={styles.button} color={googleEarthColor} onClick={googleEarth} title="Google Earth" aria-label="Google Earth">
                     Google Earth
                  </Button>
               </ButtonGroup>
            </div>

            <div className="app-flex-row-center" style={{ marginTop: 5 }}>
               <ButtonGroup variant="contained" style={{ marginRight: 10 }} aria-label="Bing toolbar buttons">
                  <Button style={styles.button} color={bingMapsColor} onClick={bingMaps} title="Bing Maps" aria-label="Bing Maps">
                     Bing Maps
                  </Button>

                  <Button style={styles.button} color={streetsideColor} onClick={streetside}
                     title="For best results, click a point between a road and the area to be viewed." aria-label="Streetside">
                     Streetside
                  </Button>
               </ButtonGroup>

               <ButtonGroup variant="contained" aria-label="Bing toolbar buttons">
                  {showPictometry && <Button variant="contained" color={pictometryColor} onClick={pictometry}
                     title="For best results, click a point between a road and the area to be viewed." aria-label="Pictometry">
                     Oblique Imagery
                  </Button>}

                  {showNearmap && <Button style={styles.button} color={nearmapColor} onClick={nearmap} title="Nearmap" aria-label="Nearmap">
                     Nearmap
                  </Button>}
               </ButtonGroup>
            </div>

         </div>
      </Paper>
   ); //end return.

} //end function.
